import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { supabase } from "@/lib/supabase"
import { useAuth } from "@/contexts/AuthContext"

const TranscribersPaymentsContext = createContext();

const initialState = {
  fetchTranscribersPayments: { loading: false, data: [], error: null, fetched: false },
  createDraftTranscriberPayment: { loading: false, data: null, error: null, fetched: false },
  setTranscriberPaymentInvoiceRowExtra: { loading: false, data: null, error: null },
  toggleTranscriberPaymentTaxes: { loading: false, data: null, error: null },
  cancelTranscriberPayment: { loading: false, data: null, error: null },
  syncTranscriberPayment: { loading: false, data: null, error: null },
  toggleTranscriberPaymentType: { loading: false, data: null, error: null },
  getUnpaidFolders: { loading: false, data: [], error: null },
  setCustomTranscriberPaymentFoldersServices: { loading: false, data: null, error: null },
  uploadTranscriberPaymentInvoice: { loading: false, data: null, error: null },
  updateTranscriberPaymentInvoiceRef: { loading: false, data: null, error: null },
  setCustomTranscriberPaymentTotal: { loading: false, data: null, error: null },
  askTranscriberPayment: { loading: false, data: null, error: null },
  getProccessingTranscriberFolders: { loading: false, data: [], error: null },
  setTranscriberPaymentInvoiceExtra: { loading: false, data: null, error: null },
  addTranscriberPaymentInvoiceExtraRow: { loading: false, data: null, error: null },
  deleteTranscriberPaymentInvoiceExtraRow: { loading: false, data: null, error: null },
  addFolderPaidService: { loading: false, data: null, error: null },
  toggleTranscriberPaymentFolderServicePospone: { loading: false, data: null, error: null }
};

export type PaymentStatus = "draft" | "asked" | "paid" | "canceled" | "declined";

export interface TranscriberPayment {
  id: string;
  transcriber_id: string;
  asked_at: string | null;
  paid_at: string | null;
  declined_at: string | null;
  declined_reason: string | null;
  canceled_at: string | null;
  status?: PaymentStatus;
  invoice: {
    is_custom: boolean;
    ref: string;
    path: string;
    total: {
      before_taxes_amount: number;
      amount: number;
      minutes: number;
      pages: number;
    };
    taxes: {
      tvq: number;
      tps: number;
      total: number;
    };
    rows: Array<{
      folder?: {
        pydio_id: string;
        rate: number;
        amount: number;
        doctor: string;
        date: string;
        minutes: number;
        seconds: number;
      };
      extra?: {
        amount: number;
        reason: string;
      };
      total: number;
    }>;
  };
}

export function formatTranscriberPayment (transcriber_payment: TranscriberPayment): TranscriberPayment {
  const formatted_transcriber_payment = { ...transcriber_payment };

  if (formatted_transcriber_payment.paid_at) formatted_transcriber_payment.status = "paid";
  else if (formatted_transcriber_payment.declined_at) formatted_transcriber_payment.status = "declined";
  else if (formatted_transcriber_payment.canceled_at) formatted_transcriber_payment.status = "canceled";
  else if (formatted_transcriber_payment.asked_at) formatted_transcriber_payment.status = "asked";
  else formatted_transcriber_payment.status = "draft";

  return formatted_transcriber_payment;
}

function updateTranscriberPayment(state, transcriber_payment) {
  const formatted_transcriber_payment = formatTranscriberPayment(transcriber_payment);

  return {
    fetchTranscribersPayments: {
      ...state.fetchTranscribersPayments,
      data: state.fetchTranscribersPayments.data.some((tp) => tp.id === transcriber_payment.id)
        ? state.fetchTranscribersPayments.data.map((tp) => (tp.id === transcriber_payment.id ? formatted_transcriber_payment : tp))
        : [formatted_transcriber_payment, ...state.fetchTranscribersPayments.data],
    },
  };
}

const getToggleTranscriberPaymentFolderServicePospone = (setState) => (transcriber_payment_id: string, { folder_id, service }: { folder_id: string, service: string }) => (
  callService({
    setState,
    stateKey: "toggleTranscriberPaymentFolderServicePospone",
    route: `toggle-transcriber-payment-folder-service-pospone`,
    body: { folder_id, service, transcriber_payment_id },
  })
)

const getAddFolderPaidService = (setState) => (transcriber_payment_id: string, { folder_id, service }: { folder_id: string, service: string }) => (
  callService({
    setState,
    stateKey: "addFolderPaidService",
    route: `add-folder-paid-service`,
    body: { folder_id, service, transcriber_payment_id },
  })
)

const getAddTranscriberPaymentInvoiceExtraRow = (setState) => (transcriber_payment_id: string, { reason, amount }: { reason: string, amount: number }) => (
  callService({
    setState,
    stateKey: "addTranscriberPaymentInvoiceExtraRow",
    route: `add-transcriber-payment-invoice-extra-row`,
    body: { reason, amount, transcriber_payment_id },
  })
)

const getDeleteTranscriberPaymentInvoiceExtraRow = (setState) => (transcriber_payment_id: string, row_index: number) => (
  callService({
    setState,
    stateKey: "deleteTranscriberPaymentInvoiceExtraRow",
    route: `delete-transcriber-payment-invoice-extra-row`,
    body: { row_index, transcriber_payment_id },
  })
)

const getFetchTranscribersPayments = (setState, transcriber) => async () => {
    setState((prevState) => ({
        ...prevState,
        fetchTranscribersPayments: { ...prevState.fetchTranscribersPayments, loading: true },
    }));

    try {
        const { data, error } = await supabase
            .from("transcribers_payments")
            .select("*")
            .eq("transcriber_id", transcriber.id)
            .order("id", { ascending: false });

        if (error) throw error;

        const formattedData = data.map(formatTranscriberPayment);

        setState((prevState) => ({
            ...prevState,
            fetchTranscribersPayments: { ...prevState.fetchTranscribersPayments, loading: false, data: formattedData, fetched: true, error: null },
        }));

        return formattedData;
    } catch (error) {
        console.error("Error fetching transcribers payments:", error);
        setState((prevState) => ({
            ...prevState,
            fetchTranscribersPayments: { ...prevState.fetchTranscribersPayments, loading: false, error },
        }));
    }
};

const getToggleTranscriberPaymentTaxes = ({ setState }) => (transcriber_payment: TranscriberPayment) => (
  callService({
    setState,
    stateKey: "toggleTranscriberPaymentTaxes",
    route: "toggle-transcriber-payment-taxes",
    body: { transcriber_payment_id: transcriber_payment.id },
  })
)

const getSetTranscriberPaymentInvoiceRowExtra = (setState, transcriber) => (body) => (
  callService({
    setState,
    stateKey: "setTranscriberPaymentInvoiceRowExtra",
    route: "set-transcriber-payment-invoice-row-extra",
    body: { transcriber_id: transcriber.id, ...body },
  })
)

const getSetTranscriberPaymentInvoiceExtra = (setState, transcriber) => (body) => (
  callService({
    setState,
    stateKey: "setTranscriberPaymentInvoiceExtra",
    route: "set-transcriber-payment-invoice-extra",
    body: { transcriber_id: transcriber.id, ...body },
  })
)

const getCreateDraftTranscriberPayment = (setState, transcriber) => () => (
  callService({
    setState,
    stateKey: "createDraftTranscriberPayment",
    route: "create-transcriber-payment",
    body: { transcriber_id: transcriber.id },
  })
)

const getCancelTranscriberPayment = (setState) => (transcriber_payment) => (
  callService({
    setState,
    stateKey: "cancelTranscriberPayment",
    route: "cancel-transcriber-payment",
    body: { transcriber_payment_id: transcriber_payment.id }
  })
)

const getSyncTranscriberPayment = (setState) => (transcriber_payment) => (
  callService({
    setState,
    stateKey: "syncTranscriberPayment",
    route: "sync-transcriber-payment",
    body: { transcriber_payment_id: transcriber_payment.id }
  })
)

const getToggleTranscriberPaymentType = (setState) => (transcriber_payment) => (
  callService({
    setState,
    stateKey: "toggleTranscriberPaymentType",
    route: "toggle-transcriber-payment-type",
    body: { transcriber_payment_id: transcriber_payment.id }
  })
)

const getGetUnpaidFolders = ({ setState, transcriber }) => () => {
  callService({
    setState,
    stateKey: "getUnpaidFolders",
    route: "get-unpaid-folders-services",
    body: { transcriber_id: transcriber.id },
    format: async json => {
      const { data, error } = await supabase
        .from("folders")
        .select(`
          id,
          doctor:doctors(id, profile:profile_id(last_name)),
          duration,
          pydio_id,
          work:works!inner (transcriber_id, reviewer_id, translater_id)
        `)
        .in("id", json.unpaid_folders_services.map(f => f.id))

      return json.unpaid_folders_services.map(({ id, service }) => ({
        ...data.find(f => f.id === id),
        service
      }))
    }
  });
}

const getSetCustomTranscriberPaymentFoldersServices = (setState) => (
  transcriber_payment: TranscriberPayment,
  folders_services: { folder_id: number, service: string }[]
) => (
  callService({
    setState,
    stateKey: "setCustomTranscriberPaymentFoldersServices",
    route: "set-custom-transcriber-payment-folders-services",
    body: { transcriber_payment_id: transcriber_payment.id, folders_services }
  })
)

const getUploadTranscriberPaymentInvoice = (setState) => async (transcriber_payment: TranscriberPayment, invoice_file: File) => {
  const formData = new FormData();
  formData.append("invoice_file", invoice_file);
  formData.append("transcriber_payment_id", transcriber_payment.id);

  return callService({
    setState,
    stateKey: "uploadTranscriberPaymentInvoice",
    route: "upload-transcriber-payment-invoice",
    body: formData,
  })
}

const getAskTranscriberPayment = (setState) => (transcriber_payment: TranscriberPayment) => (
  callService({
    setState,
    stateKey: "askTranscriberPayment",
    route: "ask-transcriber-payment",
    body: { transcriber_payment_id: transcriber_payment.id },
  })
)

const getSetCustomTranscriberPaymentTotal = (setState) => async (transcriber_payment: TranscriberPayment, total: number) => (
  callService({
    setState,
    stateKey: "setCustomTranscriberPaymentTotal",
    route: "set-custom-transcriber-payment-total",
    body: { transcriber_payment_id: transcriber_payment.id, total }
  })
)


const callService = async (
  { setState, stateKey, route, body, format }:
  { setState:any, stateKey: string, route: string, body: any, format?: (json: any) => any }
) => {
  setState((prevState) => ({
    ...prevState,
    [stateKey]: { ...prevState[stateKey], loading: true },
  }));

  const isFormData = body instanceof FormData;

  const response = await fetch(`${import.meta.env.APP_API_URL}/payments/${route}`, {
    method: "POST",
    body: isFormData ? body : JSON.stringify(body),
    headers: !isFormData ? { "Content-Type": "application/json" } : undefined,
  })

  let json = await response.json();

  if (json.error) {
    throw new Error(json.error);
  }

  json = format ? await format(json) : json;

  setState(prevState => ({
    ...prevState,
    ...(json.transcriber_payment ? updateTranscriberPayment(prevState, json.transcriber_payment) : {}),
    [stateKey]: { ...prevState[stateKey], loading: false, data: json, error: null },
  }));

  return json;
}

const getUpdateTranscriberPaymentInvoiceRef = (setState) => async (transcriber_payment: TranscriberPayment, invoice_ref: string) => {
  const { data: updated_transcriber_payment } = await supabase
    .from("transcribers_payments")
    .update({ invoice: { ...transcriber_payment.invoice, ref: invoice_ref } })
    .eq("id", transcriber_payment.id)
    .select()
    .single();

    setState((prevState) => ({
      ...prevState,
      ...updateTranscriberPayment(prevState, updated_transcriber_payment),
    }))
}

const getDownloadTranscriberPaymentInvoice = () => async (transcriber_payment: TranscriberPayment) => {
  const { data } = await supabase
    .storage
    .from('transcribers.payments.invoices')
    .getPublicUrl(transcriber_payment.invoice.path, 60, {
      download: true,
    })

  const response = await fetch(data.publicUrl);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  const filename = transcriber_payment.invoice.path.split('/').pop();
  const a = globalThis.document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  globalThis.document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(blobUrl);
}


const updateTranscriber = async ({ transcriber, setUser, transcriber_update }) => {
  const { data } = await supabase.from('transcribers').update(transcriber_update).eq('id', transcriber.id).select().single();
  
  setUser((prevState) => ({ ...prevState, transcriber: data }))
}

interface TranscriberTaxesPaymentSettings {
  enabled: boolean;
  tvq: string;
  tps: string;
}

const getUpdateTranscriberTaxesPaymentSettings = ({ setUser, transcriber }) => (taxes_payment_setting_update: Partial<TranscriberTaxesPaymentSettings>) => {
  const transcriber_update = {
    settings: {
      ...transcriber.settings,
      payment: { ...transcriber.settings?.payment, taxes: { ...(transcriber.settings?.payment?.taxes || {}), ...taxes_payment_setting_update } }
    }
  };

  updateTranscriber({ transcriber, setUser, transcriber_update });
}

interface TranscriberAddressPaymentSettings {
  address: string;
  city: string;
  state: string;
  postal_code: string;
  company_name: string;
}

const getUpdateTranscriberAddressPaymentSettings = ({ setUser, transcriber }) => (address_payment_setting_update: TranscriberAddressPaymentSettings) => {
  const transcriber_update = {
    settings: {
      ...transcriber.settings,
      payment: { ...transcriber.settings?.payment, address: { ...(transcriber.settings?.payment?.address || {}), ...address_payment_setting_update } }
    }
  };

  updateTranscriber({ transcriber, setUser, transcriber_update });
}

const getGetProccessingTranscriberFolders = ({ transcriber, setState }) => async () => {
  setState((prevState) => ({
    ...prevState,
    getProccessingTranscriberFolders: { ...prevState.getProccessingTranscriberFolders, loading: true },
  }));

  const { data: folders, error } = await supabase
    .from('folders')
    .select(`id, pydio_id, invoice_unit, works!inner(status, transcriber_id, reviewer_id, translater_id, service)`)
    .in('works.status', ['processing', 'ready'])
    .or(`transcriber_id.eq.${transcriber.id}, reviewer_id.eq.${transcriber.id}, translater_id.eq.${transcriber.id}`, { referencedTable: 'works' });

  if (error) {
    console.error("Error fetching folders:", error);
    setState((prevState) => ({
      ...prevState,
      getProccessingTranscriberFolders: { ...prevState.getProccessingTranscriberFolders, loading: false, error },
    }));
    return;
  }

  setState((prevState) => ({
    ...prevState,
    getProccessingTranscriberFolders: { ...prevState.getProccessingTranscriberFolders, loading: false, data: folders, error: null },
  }));
}

export function TranscribersPaymentsProvider({ children }) {
  const navigate = useNavigate();
  const [state, setState] = useState(initialState);
  
  const { user: { transcriber }, setUser } = useAuth();

  const updateTranscriberPaymentInvoiceRef = useCallback(getUpdateTranscriberPaymentInvoiceRef(setState), [setState]);
  const updateTranscriberTaxesPaymentSettings = useCallback(getUpdateTranscriberTaxesPaymentSettings({ setUser, transcriber }), [setUser, transcriber]);
  const updateTranscriberAddressPaymentSettings = useCallback(getUpdateTranscriberAddressPaymentSettings({ setUser, transcriber }), [setUser, transcriber]);
  const fetchTranscribersPayments = useCallback(getFetchTranscribersPayments(setState, transcriber), [setState, transcriber]);
  const createDraftTranscriberPayment = useCallback(getCreateDraftTranscriberPayment(setState, transcriber), [setState, transcriber]);
  const setTranscriberPaymentInvoiceExtra = useCallback(getSetTranscriberPaymentInvoiceExtra(setState, transcriber), [setState, transcriber]);
  const setTranscriberPaymentInvoiceRowExtra = useCallback(getSetTranscriberPaymentInvoiceRowExtra(setState, transcriber), [setState, transcriber]);
  const toggleTranscriberPaymentTaxes = useCallback(getToggleTranscriberPaymentTaxes({ setState }), [setState, updateTranscriberTaxesPaymentSettings]);
  const cancelTranscriberPayment = useCallback(getCancelTranscriberPayment(setState), [setState]);
  const syncTranscriberPayment = useCallback(getSyncTranscriberPayment(setState), [setState]);
  const toggleTranscriberPaymentType = useCallback(getToggleTranscriberPaymentType(setState), [setState]);
  const getUnpaidFolders = useCallback(getGetUnpaidFolders({ setState, transcriber }), [setState, transcriber]);
  const setCustomTranscriberPaymentFoldersServices = useCallback(getSetCustomTranscriberPaymentFoldersServices(setState), [setState]);
  const uploadTranscriberPaymentInvoice = useCallback(getUploadTranscriberPaymentInvoice(setState), [setState]);
  const askTranscriberPayment = useCallback(getAskTranscriberPayment(setState), [setState]);
  const setCustomTranscriberPaymentTotal = useCallback(getSetCustomTranscriberPaymentTotal(setState), [setState]);
  const downloadTranscriberPaymentInvoice = useCallback(getDownloadTranscriberPaymentInvoice(), [setState]);
  const getProccessingTranscriberFolders = useCallback(getGetProccessingTranscriberFolders({ transcriber, setState }), [transcriber, setState]);
  const addTranscriberPaymentInvoiceExtraRow = useCallback(getAddTranscriberPaymentInvoiceExtraRow(setState), [setState]);
  const deleteTranscriberPaymentInvoiceExtraRow = useCallback(getDeleteTranscriberPaymentInvoiceExtraRow(setState), [setState]);
  const addFolderPaidService = useCallback(getAddFolderPaidService(setState), [setState]);
  const toggleTranscriberPaymentFolderServicePospone = useCallback(getToggleTranscriberPaymentFolderServicePospone(setState), [setState]);

  useEffect(() => {
    if (state.fetchTranscribersPayments.fetched) navigate(`/payments/${state.fetchTranscribersPayments.data[0]?.id}`);
  }, [state.fetchTranscribersPayments.fetched]);

  useEffect(() => {
    fetchTranscribersPayments().then(async (transcriber_payments: TranscriberPayment[]) => {
      const draft_transcriber_payment = transcriber_payments.find((transcriber_payment) => transcriber_payment.status === "draft");

      if (!draft_transcriber_payment) await createDraftTranscriberPayment().then((json) => navigate(`/payments/${json.transcriber_payment?.id}`));
      else {
        if (!draft_transcriber_payment.invoice.is_custom)
          await syncTranscriberPayment(draft_transcriber_payment);
      }
    })
  }, []);

  const actions = {
    toggleTranscriberPaymentTaxes,
    setTranscriberPaymentInvoiceRowExtra,
    updateTranscriberTaxesPaymentSettings,
    cancelTranscriberPayment,
    syncTranscriberPayment,
    toggleTranscriberPaymentType,
    getUnpaidFolders,
    setCustomTranscriberPaymentFoldersServices,
    uploadTranscriberPaymentInvoice,
    updateTranscriberPaymentInvoiceRef,
    askTranscriberPayment,
    setCustomTranscriberPaymentTotal,
    downloadTranscriberPaymentInvoice,
    updateTranscriberAddressPaymentSettings,
    getProccessingTranscriberFolders,
    setTranscriberPaymentInvoiceExtra,
    addTranscriberPaymentInvoiceExtraRow,
    deleteTranscriberPaymentInvoiceExtraRow,
    addFolderPaidService,
    toggleTranscriberPaymentFolderServicePospone,
  }

  return (
    <TranscribersPaymentsContext.Provider value={{ state, actions }}>
      {children}
    </TranscribersPaymentsContext.Provider>
  );
}

export function useTranscribersPayments() {
    const context = useContext(TranscribersPaymentsContext);
    if (!context) {
        throw new Error("useTranscribersPayments must be used within a TranscribersPaymentsProvider");
    }
    return context;
}